import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Play, Pause, X, RotateCcw, Check, SkipForward, Square } from "lucide-react";
import { haptic } from "../lib/haptics";
import { playSetEndChime, playNextUpChime, primeChime } from "../lib/chime";

/**
 * Multi-set static hold timer with rest between sets.
 *
 * Phases per set:
 *   1. HOLD — counts DOWN from prescribedSeconds. User can:
 *        - let it hit 0 (auto-logs the full prescribed time and starts rest)
 *        - tap "Stop & log" to end the hold early (logs elapsed seconds, starts rest)
 *   2. REST — counts DOWN from restSec. User can skip to start the next set immediately.
 *   3. After the last set, shows a "Done" screen and calls onComplete with the per-set seconds array.
 *
 * Props:
 *   open, prescribedSeconds, restSec, totalSets, exerciseName, onClose, onComplete
 *   - onComplete(perSetSeconds: number[]) — called once per completed set so the parent can log
 *     incrementally, AND again with the full array when the whole exercise is done.
 *     We use the same signature for both: a single-element array on each set, then the final
 *     summary call is also a single-element array equivalent — to keep it simple, we only call
 *     onSetComplete per set (parent should react there). For a fully-done flow we call onComplete(perSetSeconds).
 */
export default function HoldTimer({
  open,
  prescribedSeconds = 30,
  restSec = 60,
  totalSets = 1,
  exerciseName = "Hold",
  onClose,
  onSetComplete, // (seconds:number, setIndex:number) — called immediately after each set's hold ends
  onComplete,    // (perSetSeconds:number[]) — called when ALL sets are done
}) {
  const [setIndex, setSetIndex] = useState(0); // 0-based
  const [phase, setPhase] = useState("hold"); // "hold" | "rest" | "done"
  const [remaining, setRemaining] = useState(prescribedSeconds);
  const [running, setRunning] = useState(false);
  const [perSet, setPerSet] = useState([]); // logged seconds per completed set
  const intervalRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSetIndex(0);
      setPhase("hold");
      setRemaining(prescribedSeconds);
      setRunning(false);
      setPerSet([]);
    }
  }, [open, prescribedSeconds]);

  // Tick
  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          // Defer phase logic to a separate effect via remaining=0
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  // When remaining hits 0, transition
  useEffect(() => {
    if (remaining !== 0 || running) return;
    if (!open) return;
    if (phase === "hold") {
      // Auto-log full prescribed seconds, then advance
      handleHoldEnd(prescribedSeconds);
    } else if (phase === "rest") {
      advanceToNextHold();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const playBeep = (kind = "set-end") => {
    if (kind === "set-end") playSetEndChime();
    else if (kind === "rest-end") playNextUpChime();
  };

  const handleHoldEnd = (loggedSec) => {
    playBeep("set-end");
    haptic.medium();
    const next = [...perSet, loggedSec];
    setPerSet(next);
    // Notify parent immediately (side effect OUTSIDE any state updater)
    onSetComplete?.(loggedSec, setIndex);
    if (setIndex + 1 >= totalSets) {
      setPhase("done");
      setRunning(false);
      // Defer the onComplete callback until after state updates flush
      setTimeout(() => {
        haptic.success();
        onComplete?.(next);
      }, 0);
    } else {
      // Move to rest, pre-incrementing setIndex so UI shows "Set N/Total" for the
      // UPCOMING set during the rest interval.
      setSetIndex(setIndex + 1);
      setPhase("rest");
      setRemaining(restSec);
      setRunning(true);
    }
  };

  const advanceToNextHold = () => {
    playBeep("rest-end");
    haptic.tap();
    // setIndex was already advanced when we entered rest; just reset to hold phase.
    setPhase("hold");
    setRemaining(prescribedSeconds);
    setRunning(false);
  };

  const stopHoldEarly = () => {
    const elapsed = prescribedSeconds - remaining;
    setRunning(false);
    // Make sure we don't double-log via the 0-effect; we are not at 0 here
    handleHoldEnd(elapsed);
  };

  const skipRest = () => {
    setRunning(false);
    advanceToNextHold();
  };

  const resetCurrentHold = () => {
    setRunning(false);
    setRemaining(prescribedSeconds);
  };

  if (!open) return null;

  const fullCycle = phase === "hold" ? prescribedSeconds : restSec;
  const pct = fullCycle > 0 ? ((fullCycle - remaining) / fullCycle) * 100 : 0;
  const currentSet = setIndex + 1;
  const isLast = currentSet >= totalSets;

  // Render via a portal at document.body so `position: fixed` resolves to the
  // viewport, not whichever ancestor has `transform: translateX(...)` (the
  // mobile carousel does this on its track, which would otherwise drag the
  // modal off-screen with the active slide).
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="hold-timer-modal"
    >
      <div className="tracing-border w-full max-w-md p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white"
          data-testid="hold-timer-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="label-eyebrow text-[#6B9BFF]">
            {phase === "done" ? "All sets complete" : phase === "rest" ? "Rest" : "Static Hold"}
          </div>
          {totalSets > 1 && (
            <div
              className="text-xs uppercase tracking-widest font-bold text-white/60 border border-white/10 px-2 py-1"
              data-testid="hold-timer-setcount"
            >
              Set {Math.min(currentSet, totalSets)} / {totalSets}
            </div>
          )}
        </div>

        <h3 className="font-display text-3xl mb-2">{exerciseName}</h3>
        <p className="text-xs text-white/50 mb-6">
          {totalSets} × {prescribedSeconds}s hold · {restSec}s rest
        </p>

        {/* Set dots */}
        {totalSets > 1 && phase !== "done" && (
          <div className="flex gap-1.5 mb-6" data-testid="hold-timer-dots">
            {Array.from({ length: totalSets }).map((_, i) => {
              const done = i < setIndex;
              const current = i === setIndex;
              return (
                <div
                  key={i}
                  className={`flex-1 h-1.5 ${
                    done ? "bg-[#6B9BFF]" : current ? "bg-[#6B9BFF]/40" : "bg-white/10"
                  }`}
                />
              );
            })}
          </div>
        )}

        <div className="relative w-56 h-56 mx-auto mb-8">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="44" fill="none" stroke="#1A1A1A" strokeWidth="8" />
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke={phase === "done" ? "#34C759" : phase === "rest" ? "#FFB020" : "#6B9BFF"}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${(pct / 100) * 276.46} 276.46`}
              style={{ transition: "stroke-dasharray 1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display text-6xl text-white tabular-nums">
              {phase === "done" ? perSet.reduce((a, b) => a + b, 0) : remaining}
            </div>
            <div className="text-xs text-white/40 mt-1 uppercase tracking-widest">
              {phase === "done"
                ? "total seconds"
                : phase === "rest"
                ? "rest"
                : "seconds"}
            </div>
          </div>
        </div>

        {/* Controls */}
        {phase === "hold" && (
          <div className="flex items-center justify-center gap-3" data-testid="hold-controls">
            <button
              onClick={() => {
                primeChime();
                setRunning((r) => !r);
              }}
              className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest px-6 py-4 inline-flex items-center gap-2 hover:bg-white"
              data-testid="hold-timer-toggle"
            >
              {running ? (
                <>
                  <Pause className="w-4 h-4" /> Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" /> {remaining < prescribedSeconds ? "Resume" : "Start"}
                </>
              )}
            </button>
            <button
              onClick={stopHoldEarly}
              disabled={remaining === prescribedSeconds}
              className="border border-white/30 text-white hover:border-[#6B9BFF] hover:text-[#6B9BFF] px-4 py-4 inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="hold-timer-stop-log"
              title="Stop hold early and log elapsed time"
            >
              <Square className="w-4 h-4" />
              <span className="hidden sm:inline text-xs uppercase tracking-wider font-bold">
                Stop & log
              </span>
            </button>
            <button
              onClick={resetCurrentHold}
              className="border border-white/20 text-white/70 hover:text-white hover:border-white/40 px-4 py-4"
              data-testid="hold-timer-reset"
              title="Reset this set's timer"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        )}

        {phase === "rest" && (
          <div className="flex items-center justify-center gap-3" data-testid="rest-controls">
            <button
              onClick={() => setRunning((r) => !r)}
              className="bg-[#FFB020] text-black font-black uppercase tracking-widest px-6 py-4 inline-flex items-center gap-2 hover:bg-white"
              data-testid="rest-timer-toggle"
            >
              {running ? (
                <>
                  <Pause className="w-4 h-4" /> Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" /> Resume
                </>
              )}
            </button>
            <button
              onClick={skipRest}
              className="border border-white/30 text-white hover:border-[#6B9BFF] hover:text-[#6B9BFF] px-5 py-4 inline-flex items-center gap-2"
              data-testid="rest-timer-skip"
            >
              <SkipForward className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider font-bold">Skip rest</span>
            </button>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center" data-testid="hold-timer-done">
            <div className="text-sm text-white/70 mb-4">
              {perSet.map((s, i) => (
                <span key={i} className="inline-block mr-2 mb-1 border border-[#6B9BFF]/30 bg-[#6B9BFF]/5 px-2 py-1 text-[#6B9BFF]">
                  Set {i + 1}: {s}s
                </span>
              ))}
            </div>
            <button
              onClick={() => onClose?.()}
              className="bg-[#34C759] text-black font-black uppercase tracking-widest px-8 py-4 inline-flex items-center gap-2"
              data-testid="hold-timer-finish"
            >
              <Check className="w-4 h-4" /> Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
